import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { requestLeave, cancelLeave } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

export const LeaveDialog = ({ open, onOpenChange, onLeaveUpdated }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    leave_start: '',
    leave_end: ''
  });
  const [loading, setLoading] = useState(false);

  const isOnLeave = user?.status === 'on_leave';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await requestLeave({
        leave_start: new Date(formData.leave_start).toISOString(),
        leave_end: new Date(formData.leave_end).toISOString()
      });
      toast.success('Leave marked successfully');
      setFormData({ leave_start: '', leave_end: '' });
      onLeaveUpdated();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark leave');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelLeave = async () => {
    setLoading(true);
    try {
      await cancelLeave();
      toast.success('Leave cancelled successfully');
      onLeaveUpdated();
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to cancel leave');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-outfit">
            {isOnLeave ? 'Manage Leave' : 'Request Leave'}
          </DialogTitle>
          <DialogDescription>
            {isOnLeave ? 'You are currently on leave' : 'Mark yourself as on leave'}
          </DialogDescription>
        </DialogHeader>

        {isOnLeave ? (
          <div className="space-y-4">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
              <p className="text-sm text-rose-900 font-medium mb-2">Current Leave Period</p>
              <p className="text-sm text-rose-700">
                From: {user.leave_start ? new Date(user.leave_start).toLocaleDateString() : 'N/A'}
              </p>
              <p className="text-sm text-rose-700">
                To: {user.leave_end ? new Date(user.leave_end).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            <Button
              onClick={handleCancelLeave}
              disabled={loading}
              data-testid="cancel-leave-button"
              className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-full h-11"
            >
              {loading ? 'Cancelling...' : 'Cancel Leave'}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <div className="space-y-2">
              <Label htmlFor="leave_start">Leave Start Date *</Label>
              <Input
                id="leave_start"
                type="date"
                data-testid="leave-start-input"
                value={formData.leave_start}
                onChange={(e) => setFormData({ ...formData, leave_start: e.target.value })}
                required
                className="h-11 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="leave_end">Leave End Date *</Label>
              <Input
                id="leave_end"
                type="date"
                data-testid="leave-end-input"
                value={formData.leave_end}
                onChange={(e) => setFormData({ ...formData, leave_end: e.target.value })}
                required
                min={formData.leave_start}
                className="h-11 rounded-xl"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="submit-leave-request"
                disabled={loading}
                className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white rounded-full px-6"
              >
                {loading ? 'Submitting...' : 'Mark Leave'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
